import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, FileText, Lock, Unlock } from 'lucide-react';
import { useAuthStore } from '../../store/useStore';
import { refertiProdottiAPI } from '../../services/refertiProdottiApi';

/**
 * RefertiProcedura — la sezione «Referti / Prescrizioni» di una
 * procedura: i referti e le ricette nati da qui, con la matita che
 * apre l'editor.
 *
 * LA RICETTA E' UN DOCUMENTO DI TESTO, ed e' un referto con tipo
 * «ricetta» (migration 188): stesso editor, stessa intestazione, stessa
 * libreria di testi, stesso PDF congelato alla conclusione. Non c'e'
 * una banca dati di farmaci, un controllo di dose, un suggerimento: il
 * giorno in cui il gestionale suggerisce o segnala, diventa un
 * dispositivo medico (MDR, regola 11), e non e' una decisione da
 * prendere qui. La vecchia tabella «Farmaco / Dosaggio / Frequenza /
 * Durata» e' sparita per questo.
 */
const dataIta = (v) => (v ? new Date(String(v).slice(0, 10)).toLocaleDateString('it-IT') : '');

const RefertiProcedura = ({ visita, azioni }) => {
    const utente = useAuthStore((s) => s.user);
    const navigate = useNavigate();
    const [voci, setVoci] = useState([]);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);

    const carica = useCallback(async () => {
        try {
            const r = await refertiProdottiAPI.elenco(visita.paziente_id, undefined, { visita_id: visita.id });
            setVoci(r.data?.data?.referti || []);
        } catch (e) { /* la sezione resta vuota; l'errore vero lo da' l'editor */ }
    }, [visita.id, visita.paziente_id]);

    useEffect(() => { carica(); }, [carica]);

    const nuovaRicetta = async () => {
        setInCorso(true);
        setErrore(null);
        try {
            const r = await refertiProdottiAPI.crea({
                tipo: 'ricetta', paziente_id: visita.paziente_id, visita_id: visita.id, titolo: 'Ricetta',
            });
            navigate(`/referti/${r.data.data.referto.id}`);
        } catch (e) {
            setErrore(e?.response?.data?.message || e?.message || 'Errore nella creazione della ricetta');
            setInCorso(false);
        }
    };

    return (
        <>
            <div className="pd-referti-azioni">
                {azioni}
                {utente?.medico_id && (
                    <button className="pd-add" onClick={nuovaRicetta} disabled={inCorso}
                            title="Una ricetta è un documento di testo: si scrive nell'editor e si stampa con l'intestazione">
                        {inCorso ? 'Creo la ricetta…' : '+ Ricetta'}
                    </button>
                )}
            </div>
            {errore && <p className="pd-errore-inline">{errore}</p>}
            {voci.length === 0 ? (
                <p className="pd-text pd-text--muted">Nessun referto o ricetta in questa procedura.</p>
            ) : (
                <ul className="pd-referti">
                    {voci.map((r) => (
                        <li key={r.id} className="pd-referto">
                            <FileText size={14} aria-hidden="true" />
                            <span className="pd-referto-tipo">{r.tipo === 'ricetta' ? 'Ricetta' : 'Referto'}</span>
                            <span className="pd-referto-titolo">{r.titolo}</span>
                            <span className="pd-referto-data">{dataIta(r.data_referto)}</span>
                            <span className={`pd-referto-stato ${r.stato}`}>
                                {r.stato === 'bozza' ? <><Unlock size={12} /> Bozza</> : <><Lock size={12} /> Concluso</>}
                            </span>
                            <button className="btn-edit-small" title={r.stato === 'bozza' ? 'Apri nell\'editor' : 'Apri'}
                                    onClick={() => navigate(`/referti/${r.id}`)}>
                                <Pencil size={15} strokeWidth={1.8} aria-hidden="true" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
};

export default RefertiProcedura;
